import { definedMap } from "@glideapps/ts-necessities";

import {
    ComponentKind,
    TableColumn,
    LegacyPropertyDescription,
    InputOutputTables,
    getTableColumnDisplayName,
    MutatingScreenKind,
    TableGlideType,
    ActionComponentDescription,
    makeEmptyComponentDescription,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { assert } from "../../Support";
import { ComponentDescriptor, ComponentBuilder, ComponentConfiguratorContext, PropertyDescriptor } from ".";
import { actionsNeedScreenContext } from "../actions";
import { ActionSource } from "../../components/types";
import { makeColumnProperty, makeStringProperty } from "./property-utils";
import { makeCaptionStringPropertyDescriptor, makeTextPropertyDescriptor } from "./descriptor-utils";
import { ListItemFlags } from "../array-screens/list-item-flags";
import { getDocURL } from "../../docUrl";
import { makeText } from "../builder-utils";
import { ActionComponentHandlerBase } from "./action-component";

const ComponentKindEmail: ComponentKind = "email";

// FIXME: Unify this with `DeliverEmailActionDescription`
export interface AdditionalEmailProperties {
    readonly subjectProperty?: LegacyPropertyDescription;
    readonly bodyProperty?: LegacyPropertyDescription;
    readonly ccProperty?: LegacyPropertyDescription;
    readonly bccProperty?: LegacyPropertyDescription;
}

export function makeEmailPropertyDescriptors(
    emailPropertyName: string,
    withAdditionalProperties: boolean,
    mutatingScreenKind: MutatingScreenKind | undefined
): readonly PropertyDescriptor[] {
    const props: PropertyDescriptor[] = [
        makeTextPropertyDescriptor(emailPropertyName, "To", "Recipient", true, mutatingScreenKind, {
            preferredType: "email-address",
            isDefaultCaption: true,
            columnFirst: true,
            preferredNames: ["email"],
        }),
    ];

    if (withAdditionalProperties) {
        props.push(
            makeTextPropertyDescriptor("subjectProperty", "Subject", "Email subject", false, mutatingScreenKind, {
                emptyByDefault: true,
                searchable: false,
                columnFirst: true,
            }),
            makeTextPropertyDescriptor("bodyProperty", "Body", "Email text", false, mutatingScreenKind, {
                emptyByDefault: true,
                searchable: false,
                columnFirst: true,
            }),
            makeTextPropertyDescriptor("ccProperty", "CC", "Other recipients", false, mutatingScreenKind, {
                preferredType: "email-address",
                emptyByDefault: true,
                searchable: false,
                columnFirst: true,
            }),
            makeTextPropertyDescriptor("bccProperty", "BCC", "Silent recipients", false, mutatingScreenKind, {
                preferredType: "email-address",
                emptyByDefault: true,
                searchable: false,
                columnFirst: true,
            })
        );
    }

    return props;
}

export function makeEmailValues(
    builder: ComponentBuilder,
    tables: InputOutputTables,
    toDesc: LegacyPropertyDescription | undefined,
    desc: AdditionalEmailProperties | undefined,
    context?: SerializedComponent
):
    | {
          readonly to: SerializedComponent;
          readonly subject: SerializedComponent | undefined;
          readonly body: SerializedComponent | undefined;
          readonly cc: SerializedComponent | undefined;
          readonly bcc: SerializedComponent | undefined;
      }
    | undefined {
    const to = makeText(builder, toDesc, tables.input, true, false, true, false, context);
    if (to === undefined) return undefined;

    const subject = makeText(builder, desc?.subjectProperty, tables.input, true, false, true, false, context);
    const body = makeText(builder, desc?.bodyProperty, tables.input, true, false, true, false, context);
    const cc = makeText(builder, desc?.ccProperty, tables.input, true, false, true, false, context);
    const bcc = makeText(builder, desc?.bccProperty, tables.input, true, false, true, false, context);

    return { to, subject, body, cc, bcc };
}

interface EmailComponentDescription extends ActionComponentDescription, AdditionalEmailProperties {
    readonly propertyName: LegacyPropertyDescription;
    readonly caption: LegacyPropertyDescription | undefined;
}

export class EmailComponentHandler extends ActionComponentHandlerBase<EmailComponentDescription> {
    constructor() {
        super(ComponentKindEmail);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: EmailComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const emailValues = makeEmailValues(builder, tables, desc.propertyName, desc);
        if (emailValues === undefined) return b.emptyUI;

        // FIXME: Make a `let` for `to`, so we don't get the property twice.
        const { to } = emailValues;
        const toWithFormat = makeText(builder, desc.propertyName, tables.input, true, true, true);
        assert(toWithFormat !== undefined);

        const actionLets: [string, SerializedComponent][] = [];

        let { subject, body, cc, bcc } = emailValues;
        if (subject !== undefined) {
            actionLets.push(["subject", subject]);
            subject = b.getVariable("subject");
        }
        if (body !== undefined) {
            actionLets.push(["body", body]);
            body = b.getVariable("body");
        }
        if (cc !== undefined) {
            actionLets.push(["cc", cc]);
            cc = b.getVariable("cc");
        }
        if (bcc !== undefined) {
            actionLets.push(["bcc", bcc]);
            bcc = b.getVariable("bcc");
        }

        const { actions } = this.getActions(desc, tables.input, builder, mutatingScreenKind);
        const screenContext = definedMap(actions, a =>
            actionsNeedScreenContext(a, tables, builder, mutatingScreenKind) ? b.getContext : undefined
        );

        const onTaps: SerializedComponent[] = [];

        // FIXME: Email doesn't yet work with actions that don't require the screen context.
        // We're trying to get away from those anyway, though.
        const actionOnTap = builder.makeSequenceFromActions(
            actions,
            tables,
            screenContext,
            outputContext,
            mutatingScreenKind,
            false
        );
        if (actionOnTap !== undefined) {
            onTaps.push(actionOnTap);
        }

        onTaps.push(b.sendEmail(to, subject, body, cc, bcc));

        let action = b.sequence(onTaps);
        if (actionLets.length > 0) {
            action = b.let(actionLets, action);
        }

        return b.ifIsNotEmpty(
            to,
            b.adaptiveListItem(
                toWithFormat,
                makeText(builder, desc.caption, tables.input, true),
                undefined,
                undefined,
                b.icon("mail"),
                undefined,
                action,
                undefined,
                ActionSource.SendEmail,
                b.constant(ListItemFlags.InvertTitleAndSubtitle),
                undefined,
                undefined,
                undefined
            )
        );
    }

    public getDescriptor(
        _desc: EmailComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<EmailComponentDescription> {
        return {
            name: "Email",
            description: "Display and compose an email",
            img: "componentEmail",
            group: "Buttons",
            helpUrl: getDocURL("email"),
            properties: [
                makeTextPropertyDescriptor("propertyName", "To", "Recipient", true, mutatingScreenKind, {
                    preferredType: "email-address",
                    isDefaultCaption: true,
                    columnFirst: true,
                }),
                makeTextPropertyDescriptor("subjectProperty", "Subject", "Sender", false, mutatingScreenKind, {
                    emptyByDefault: true,
                    searchable: false,
                    columnFirst: true,
                }),
                makeTextPropertyDescriptor("bodyProperty", "Body", "Email text", false, mutatingScreenKind, {
                    emptyByDefault: true,
                    searchable: false,
                    columnFirst: true,
                }),
                makeTextPropertyDescriptor("ccProperty", "CC", "Other recipients", false, mutatingScreenKind, {
                    preferredType: "email-address",
                    emptyByDefault: true,
                    searchable: false,
                    columnFirst: true,
                }),
                makeTextPropertyDescriptor("bccProperty", "BCC", "Silent recipients", false, mutatingScreenKind, {
                    preferredType: "email-address",
                    emptyByDefault: true,
                    searchable: false,
                    columnFirst: true,
                }),
                makeCaptionStringPropertyDescriptor("Email", false, mutatingScreenKind),
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }

    public static defaultComponent(column: TableColumn): EmailComponentDescription {
        return {
            ...makeEmptyComponentDescription(ComponentKindEmail),
            propertyName: makeColumnProperty(column.name),
            subjectProperty: undefined,
            bodyProperty: undefined,
            ccProperty: undefined,
            bccProperty: undefined,
            caption: makeStringProperty(getTableColumnDisplayName(column)),
            actions: [],
        };
    }
}
